import { useState } from 'react';

import { useAppDispatch } from 'dux/app/hooks';

import { Box, Button, Modal, Text } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { Badge } from 'Components/Badge';
import { Image } from 'Components/Image';

import { content } from 'assets/content/promotions/freeHairSupplements';
import closeIcon from 'assets/images/pop-up-close-button.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';
import { freeSupplementsModalClosed, freeSupplementsModalOpened } from 'dux/user/actions';

const CloseModalButton = styled.button`
  cursor: pointer;
  position: absolute;
  top: 12px;
  right: 12px;
  appearance: none;
  background: none;
  border: none;
  padding: 0;
`;

export const FreeProductModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { isTablet, isDesktop } = useResponsiveVariant();
  const dispatch = useAppDispatch();

  return (
    <Modal
      asChild
      events={{
        onModalClose: () => {
          dispatch(freeSupplementsModalClosed());
          dispatch(trackHeapEvent('Free Supplements Offer Pop-Up - View'));
        },
        onModalOpen: () => {
          dispatch(freeSupplementsModalOpened());
          dispatch(trackHeapEvent('Free Supplements Offer Pop-Up - Close'));
        },
        timer: {
          onModalOpenAfterTime: () =>
            dispatch(trackHeapEvent('Free Supplements Offer Pop-Up - View (2 sec)')),
        },
      }}
      hideClose
      isOpen={isOpen}
      onClose={onClose}
    >
      <Box
        __maxWidth={isTablet || isDesktop ? '555px' : 'none'}
        __overflow="auto"
        __zIndex="10000"
        borderRadius="10"
        padding="none"
      >
        <Box
          bgColor="neutral.200"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          position="relative"
          width="full"
        >
          <Badge left="4x" size="88px" top="4x">
            <Text
              __width="min-content"
              color="neutral.900"
              textAlign="center"
              variant="label3medium"
            >
              {content.badge.label}
            </Text>
          </Badge>
          <CloseModalButton
            data-click="exit"
            data-from="free-supplements-pop-up"
            data-testid="close-modal"
            id="modal-close-button"
            onClick={onClose}
          >
            <img alt="Close" src={closeIcon} />
          </CloseModalButton>
          <Image
            alt={content.image.alt}
            src={content.image.src}
            style={{ width: '100%', height: '100%' }}
          />
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            gap={{ mobile: '2x', tablet: '3x' }}
            paddingX={{ mobile: '5x', tablet: '10x' }}
            paddingY={{ mobile: '12x', tablet: '8x' }}
          >
            <Text as="h2" color="primary.400" textAlign="center" variant="h3">
              {content.title}
            </Text>
            <Text color="primary.400" textAlign="center" variant="body4regular">
              {content.description}
            </Text>
            <Button
              capsule
              data-click="offer-cta"
              data-from="free-supplements-pop-up"
              onClick={onClose}
              size="flexible"
              variant="primary"
            >
              {content.cta.label}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export const FreeProductModalWithStateLogic = ({ isOpen }: { isOpen: boolean }) => {
  const [open, setOpen] = useState(isOpen);
  const onClose = () => setOpen(false);
  return <FreeProductModal isOpen={open} onClose={onClose} />;
};
